import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { ReservationFacility } from '../../firestore-models/reservation-facility';
import { ApiReservationFacility, ApiReservationQuestion } from '../../api-client-module';
import { ReservationFacilityAttribute } from '../../firestore-models/reservation-facility-attribute';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-reservation-facilities-dialog',
  templateUrl: './select-reservation-facilities-dialog.component.html',
  styleUrls: ['./select-reservation-facilities-dialog.component.css'],
  standalone: false,
})
export class SelectReservationFacilitiesDialogComponent implements OnInit, OnDestroy {

  projectLanguages!: Array<ProjectLanguage>;

  private reservationFacilitiesCollection!: AngularFirestoreCollection<ReservationFacility>;
  reservationFacilitiesObservable!: Observable<ReservationFacility[]>;
  reservationFacilitiesSubscribe!: Subscription;
  private reservationFacilityAttributesCollection!: AngularFirestoreCollection<ReservationFacilityAttribute>;
  reservationFacilityAttributesObservable!: Observable<ReservationFacilityAttribute[]>;
  reservationFacilityAttributesSubscribe!: Subscription;
  projectId: string;

  attrCollectionName = AppConst.FIRESTORE_RESERVATION_FACILITY_ATTR;

  displayedColumns: string[] = ['position', 'select', 'reservation_facility_id', 'attribute', 'name'];
  dataSource = new MatTableDataSource<ReservationFacility>();
  selection = new SelectionModel<ReservationFacility>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'reservationFacilityAttributes';
  reservationFacilityAttributes!: Array<ReservationFacilityAttribute>;

  searchFilter: any = {};

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<SelectReservationFacilitiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiReservationFacility,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 予約設備取得
    this.reservationFacilitiesCollection = this.angularFirestore.collection<ReservationFacility>(
      AppConst.FIRESTORE_RESERVATION_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('reservation_facility_id', 'asc'));
    this.reservationFacilitiesObservable = this.reservationFacilitiesCollection.valueChanges({ idField: 'id' });
    this.reservationFacilitiesSubscribe = this.reservationFacilitiesObservable.subscribe(reservationFacilities => {
      this.dataSource.data = this.addFilterValue(reservationFacilities);
    });
    // 予約設備属性取得
    this.reservationFacilityAttributesCollection = this.angularFirestore.collection<ReservationFacilityAttribute>(
      this.attrCollectionName, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.reservationFacilityAttributesObservable = this.reservationFacilityAttributesCollection.valueChanges({ idField: 'id' });
    this.reservationFacilityAttributesSubscribe = this.reservationFacilityAttributesObservable.subscribe(reservationFacilityAttributes => {
      this.reservationFacilityAttributes = reservationFacilityAttributes;
    });
  }

  ngOnDestroy(): void {
    this.reservationFacilitiesSubscribe.unsubscribe();
    this.reservationFacilityAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ReservationFacility): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectReservationFacilities() {
    let reservationFacilities: Array<ApiReservationFacility> = new Array<ApiReservationFacility>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        let questions: Array<ApiReservationQuestion> = new Array<ApiReservationQuestion>();
        if (row.questions && row.questions.length > 0) {
          for( const question of row.questions) {
            const question_check_date_range_upper: any = question.question_check_date_range_upper ? new Date(question!.question_check_date_range_upper.seconds * 1000) : null;
            const question_check_date_range_lower: any = question.question_check_date_range_lower ? new Date(question!.question_check_date_range_lower.seconds * 1000) : null;
            const question_check_base_date: any = question.question_check_base_date ? new Date(question!.question_check_base_date.seconds * 1000) : null;
            let apiQuestion: ApiReservationQuestion = {
              question_id: question.question_id,
              display_no: question.display_no,
              question_names: question.question_names,
              question_comps: question.question_comps,
              question_type: question.question_type,
              question_select_items: question.question_select_items,
              question_check: question.question_check,
              question_check_range_upper: question.question_check_range_upper,
              question_check_range_lower: question.question_check_range_lower,
              question_check_date_range_upper: question_check_date_range_upper,
              question_check_date_range_lower: question_check_date_range_lower,
              question_check_base_date: question_check_base_date,
              question_check_base_date_to_start: question.question_check_base_date_to_start,
              question_check_base_date_to_end: question.question_check_base_date_to_end,
              question_check_col_count_upper: question.question_check_col_count_upper,
              question_check_col_count_lower: question.question_check_col_count_lower,
              project_id: question.project_id,
              enabled: question.enabled,
            };
            questions.push(apiQuestion);
          }
        }
        const reservationFacility: ApiReservationFacility = {
          id: row.id,
          reservation_facility_id: row.reservation_facility_id,
          reservation_facility_attribute_id: row.reservation_facility_attribute_id,
          reservation_facility_names: row.reservation_facility_names,
          reservation_facility_descs: row.reservation_facility_descs,
          reservation_user_input_titles: row.reservation_user_input_titles,
          reservation_send_mail_titles: row.reservation_send_mail_titles,
          reservation_send_mail_messages1: row.reservation_send_mail_messages1,
          reservation_send_mail_messages2: row.reservation_send_mail_messages2,
          reservation_send_mail_messages3: row.reservation_send_mail_messages3,
          secretariat_mail_addresses: row.secretariat_mail_addresses,
          reservation_secretariat_mail_messages1: row.reservation_secretariat_mail_messages1,
          is_exhibit_info_message: row.is_exhibit_info_message,
          is_hide_reservation_cancel_date_message: row.is_hide_reservation_cancel_date_message,
          is_hide_reservation_send_mail_cancel_message: row.is_hide_reservation_send_mail_cancel_message,
          reservation_cancel_mail_titles: row.reservation_cancel_mail_titles,
          reservation_cancel_mail_messages1: row.reservation_cancel_mail_messages1,
          reservation_cancel_mail_messages2: row.reservation_cancel_mail_messages2,
          reservation_cancel_mail_messages3: row.reservation_cancel_mail_messages3,
          reservation_secretariat_cancel_mail_messages1: row.reservation_secretariat_cancel_mail_messages1,
          remind_days: row.remind_days,
          reservation_remind_mail_titles: row.reservation_remind_mail_titles,
          reservation_remind_mail_messages1: row.reservation_remind_mail_messages1,
          reservation_remind_mail_messages2: row.reservation_remind_mail_messages2,
          reservation_remind_mail_messages3: row.reservation_remind_mail_messages3,
          reservation_facility_files: row.reservation_facility_files,
          latitude: row.reservation_facility_geopoint.latitude,
          longitude: row.reservation_facility_geopoint.longitude,
          show_marker: row.show_marker,
          project_id: row.project_id,
          reservation_method: row.reservation_method,
          lottery_method: row.lottery_method,
          reservation_cancel: row.reservation_cancel,
          reservation_unit: row.reservation_unit,
          reservation_count: row.reservation_count,
          disp_start_date: row.disp_start_date ? row.disp_start_date.toDate() : null,
          disp_end_date: row.disp_end_date ? row.disp_end_date.toDate() : null,
          reservation_start_date: row.reservation_start_date,
          reservation_end_date: row.reservation_end_date,
          is_hide_reservation_end_date: row.is_hide_reservation_end_date,
          holidays: row.holidays,
          reservation_select_times: row.reservation_select_times,
          enabled: row.enabled,
          is_visibled_company_name: row.is_visibled_company_name,
          is_visibled_department: row.is_visibled_department,
          is_visibled_job_title: row.is_visibled_job_title,
          is_visibled_surname_chinese: row.is_visibled_surname_chinese,
          is_visibled_name_chinese: row.is_visibled_name_chinese,
          is_visibled_family_name: row.is_visibled_family_name,
          is_visibled_first_name: row.is_visibled_first_name,
          is_visibled_gender: row.is_visibled_gender,
          is_visibled_birthday: row.is_visibled_birthday,
          is_visibled_address: row.is_visibled_address,
          is_visibled_mobile_phone_number: row.is_visibled_mobile_phone_number,
          is_visibled_user_request: row.is_visibled_user_request,
          is_hide_reservation_condition: row.is_hide_reservation_condition,
          questions: questions,
          is_multi_select: row.is_multi_select,
          reservation_on_hold_mail_titles: row.reservation_on_hold_mail_titles,
          reservation_on_hold_mail_messages1: row.reservation_on_hold_mail_messages1,
          reservation_on_hold_mail_messages2: row.reservation_on_hold_mail_messages2,
          reservation_on_hold_mail_messages3: row.reservation_on_hold_mail_messages3,
          reservation_lost_mail_titles: row.reservation_lost_mail_titles,
          reservation_lost_mail_messages1: row.reservation_lost_mail_messages1,
          reservation_lost_mail_messages2: row.reservation_lost_mail_messages2,
          reservation_lost_mail_messages3: row.reservation_lost_mail_messages3,
          reservation_lottery_date: row.reservation_lottery_date,
          splats_api_type: row.splats_api_type,
          splats_key_device_id: row.splats_key_device_id,
          splats_unit_no: row.splats_unit_no,
          splats_borrow_grace_minutes: row.splats_borrow_grace_minutes,
          splats_return_grace_minutes: row.splats_return_grace_minutes,
          is_visibled_splats_qr_company_name: row.is_visibled_splats_qr_company_name,
          is_visibled_splats_qr_department: row.is_visibled_splats_qr_department,
          is_visibled_splats_qr_job_title: row.is_visibled_splats_qr_job_title,
          is_visibled_splats_qr_surname_name_chinese: row.is_visibled_splats_qr_surname_name_chinese,
          is_visibled_splats_qr_family_first_name: row.is_visibled_splats_qr_family_first_name,
          is_visibled_splats_qr_gender: row.is_visibled_splats_qr_gender,
          is_visibled_splats_qr_birthday: row.is_visibled_splats_qr_birthday,
          is_visibled_splats_qr_address: row.is_visibled_splats_qr_address,
          is_visibled_splats_qr_mobile_phone_number: row.is_visibled_splats_qr_mobile_phone_number,
          is_visibled_splats_qr_user_request: row.is_visibled_splats_qr_user_request,
        };
        reservationFacilities.push(reservationFacility);
      }
    }
    this.dialogRef.close(reservationFacilities);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'reservation_facility_id':
          return compare(a.reservation_facility_id, b.reservation_facility_id, isAsc);
        case 'attribute':
          const aAttr = a.reservation_facility_attribute_id ? a.reservation_facility_attribute_id : '';
          const bAttr = b.reservation_facility_attribute_id ? b.reservation_facility_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.reservation_facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.reservation_facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.reservation_facility_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.reservation_facility_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
